import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from "@/Components/ui/input";
import { Button } from "@/Components/ui/button";
import StyledSwitch from "../Components/Layout/StyledSwitch";
import { ChevronRight, ChevronDown, ArrowLeft, Building2, Plus } from "lucide-react";
import axiosInstance from '../utils/axiosConfig';
import logo from '../images/pslogo.png';

const Register = () => {
    const navigate = useNavigate();
    const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [createNewCompany, setCreateNewCompany] = useState(false);
    const [isDepartmentDropdownOpen, setIsDepartmentDropdownOpen] = useState(false);
    const [formData, setFormData] = useState({
        firstname: '',
        lastname: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: '',
        department: '',
        companyCode: '',
        newsupdates: false,
        company: {
            name: '',
            address: '',
            zipcode: '',
            city: '',
            phonenumber: ''
        }
    });

    const roles = [
        { id: 'owner', name: 'Eigenaar' },
        { id: 'chef', name: 'Chef' },
        { id: 'manager', name: 'Manager' },
        { id: 'employee', name: 'Werknemer' },
    ];

    const departments = [
        { id: 'kitchen', name: 'Keuken' },
        { id: 'service', name: 'Service' },
    ];

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name.startsWith('company.')) {
            const companyField = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                company: {
                    ...prev.company,
                    [companyField]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setSuccess('');

        if (formData.password !== formData.confirmPassword) {
            setError('Wachtwoorden komen niet overeen');
            setIsLoading(false);
            return;
        }

        try {
            const payload = {
                firstname: formData.firstname,
                lastname: formData.lastname,
                email: formData.email,
                password: formData.password,
                role: formData.role,
                department: formData.department,
                newsupdates: formData.newsupdates
            };

            if (createNewCompany) {
                payload.company = {
                    name: formData.company.name,
                    address: formData.company.address,
                    zipCode: formData.company.zipcode,
                    city: formData.company.city,
                    phoneNumber: formData.company.phonenumber
                };
            } else {
                payload.companyCode = formData.companyCode;
            }

            const response = await axiosInstance.post('/auth/Register', payload);
            if (response.data.success) {
                setSuccess('U ontvangt binnen enkele minuten een e-mail met activatiegegevens voor je account.');

                // Reset form
                setFormData({
                    firstname: '',
                    lastname: '',
                    email: '',
                    password: '',
                    confirmPassword: '',
                    role: 'employee',
                    department: 'kitchen',
                    companyCode: '',
                    newsupdates: false,
                    company: {
                        name: '',
                        address: '',
                        zipcode: '',
                        city: '',
                        phonenumber: ''
                    }
                });
            } else {
                // Als success false is, toon de error
                setError(response.data.error);
            }
        } catch (err) {
            console.log('Registration error:', err);
            if (err.response) {
                setError(err.response.data || 'Er ging iets mis bij het registreren.');
            } else if (err.request) {
                setError('Kan geen verbinding maken met de server.');
            } else {
                setError('Er ging iets mis bij het registreren.');
            }
        } finally {
            setIsLoading(false);
            window.scrollTo({ top: 0, behavior: 'smooth' }); 
        } 
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12">
            <div className="max-w-2xl w-full space-y-6 p-8 bg-white rounded-lg shadow-lg relative">
                <div className="absolute left-[15px] top-[-5px] w-[80px]">
                    <img src={logo} />
                </div>
                {/* Header Section */}
                <div className="text-center">
                    <h2 className="text-3xl font-inter text-ps-blue-dark">
                        Registreren
                    </h2>
                    <p className="mt-2 text-sm text-gray-600 font-inter">
                        Maak een nieuw account aan
                    </p>
                </div>

                {/* Form Section */}
                <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                    {error && (
                        <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded font-inter">
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="text-green-600 text-sm text-center bg-green-50 p-3 rounded font-inter">
                            {success}
                        </div>
                    )}

                    {/* Persoonlijke gegevens */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                Voornaam
                            </label>
                            <Input
                                type="text"
                                name="firstname"
                                required
                                value={formData.firstname}
                                onChange={handleChange}
                                className="w-full p-2 border rounded bg-white font-inter"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                Achternaam
                            </label>
                            <Input
                                type="text"
                                name="lastname"
                                required
                                value={formData.lastname}
                                onChange={handleChange}
                                className="w-full p-2 border rounded bg-white font-inter"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                            E-mailadres
                        </label>
                        <Input
                            type="email"
                            name="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            className="w-full p-2 border rounded bg-white font-inter"
                            placeholder="naam@bedrijf.nl"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                Wachtwoord
                            </label>
                            <Input
                                type="password"
                                name="password"
                                minLength={8}
                                required
                                value={formData.password}
                                onChange={handleChange}
                                className="w-full p-2 border rounded bg-white font-inter"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                Bevestig wachtwoord
                            </label>
                            <Input
                                type="password"
                                name="confirmPassword"
                                minLength={8}
                                required
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                className="w-full p-2 border rounded bg-white font-inter"
                            />
                        </div>
                    </div>


                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="relative">
                            <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                Rol werknemer
                            </label>
                            <div className="relative">
                                <button
                                    type="button"
                                    className="w-full flex items-center justify-between p-2 border rounded bg-white text-left"
                                    onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
                                >
                                    <span className="font-inter">
                                        {roles.find(r => r.id === formData.role)?.name || "Selecteer een rol"}
                                    </span>
                                    <ChevronDown className="h-4 w-4" />
                                </button>
                                {isRoleDropdownOpen && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg">
                                        {roles.map((role) => (
                                            <div
                                                key={role.id}
                                                className="p-2 hover:bg-gray-100 cursor-pointer font-inter"
                                                onClick={() => {
                                                    setFormData(prev => ({ ...prev, role: role.id }));
                                                    setIsRoleDropdownOpen(false);
                                                }}
                                            >
                                                {role.name}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="relative">
                            <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                Afdeling
                            </label>
                            <div className="relative">
                                <button
                                    type="button"
                                    className="w-full flex items-center justify-between p-2 border rounded bg-white text-left"
                                    onClick={() => setIsDepartmentDropdownOpen(!isDepartmentDropdownOpen)}
                                >
                                    <span className="font-inter">
                                        {departments.find(d => d.id === formData.department)?.name || "Selecteer een afdeling"}
                                    </span>
                                    <ChevronDown className="h-4 w-4" />
                                </button>
                                {isDepartmentDropdownOpen && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg">
                                        {departments.map((department) => (
                                            <div
                                                key={department.id}
                                                className="p-2 hover:bg-gray-100 cursor-pointer font-inter"
                                                onClick={() => {
                                                    setFormData(prev => ({ ...prev, department: department.id }));
                                                    setIsDepartmentDropdownOpen(false);
                                                }}
                                            >
                                                {department.name}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>


                    {/* Bedrijf sectie */}
                    <div className="pt-4">
                        <div className="flex gap-4 mb-6">
                            <Button
                                type="button"
                                onClick={() => setCreateNewCompany(false)}
                                className={`flex-1 flex items-center justify-center gap-2 ${!createNewCompany ? 'bg-ps-blue-dark text-white' : 'bg-gray-100'}`}
                            >
                                <Building2 className="h-4 w-4" />
                                Join bestaand bedrijf
                            </Button>
                            <Button
                                type="button"
                                onClick={() => setCreateNewCompany(true)}
                                className={`flex-1 flex items-center justify-center gap-2 ${createNewCompany ? 'bg-ps-blue-dark text-white' : 'bg-gray-100'}`}
                            >
                                <Plus className="h-4 w-4" />
                                Nieuw bedrijf
                            </Button>
                        </div>

                        {createNewCompany ? (
                            <div className="space-y-4 bg-blue-50 p-4 rounded">
                                <div>
                                    <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                        Bedrijfsnaam
                                    </label>
                                    <Input
                                        type="text"
                                        name="company.name"
                                        required
                                        value={formData.company.name}
                                        onChange={handleChange}
                                        className="w-full p-2 border rounded bg-white font-inter"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                        Adres
                                    </label>
                                    <Input
                                        type="text"
                                        name="company.address"
                                        required
                                        value={formData.company.address}
                                        onChange={handleChange}
                                        className="w-full p-2 border rounded bg-white font-inter"
                                    />
                                </div>
                                <div className="grid grid-cols-4 gap-4">
                                    <div className="col-span-1">
                                        <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                            Postcode
                                        </label>
                                        <Input
                                            type="text"
                                            name="company.zipcode"
                                            required
                                            value={formData.company.zipcode}
                                            onChange={handleChange}
                                            className="w-full p-2 border rounded bg-white font-inter"
                                        />
                                    </div>
                                    <div className="col-span-3">
                                        <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                            Plaats
                                        </label>
                                        <Input
                                            type="text"
                                            name="company.city"
                                            required
                                            value={formData.company.city}
                                            onChange={handleChange}
                                            className="w-full p-2 border rounded bg-white font-inter"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                        Telefoonnummer
                                    </label>
                                    <Input
                                        type="tel"
                                        name="company.phonenumber"
                                        required
                                        value={formData.company.phonenumber}
                                        onChange={handleChange}
                                        className="w-full p-2 border rounded bg-white font-inter"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div>
                                <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                    Bedrijfscode
                                </label>
                                <Input
                                    type="text"
                                    name="companyCode"
                                    required
                                    value={formData.companyCode}
                                    onChange={handleChange}
                                    className="w-full p-2 border rounded bg-white font-inter"
                                    placeholder="Voer de code in die je van je bedrijf hebt ontvangen"
                                />
                            </div>
                        )}
                    </div>

                    {/* Nieuwsbrief opt-in */}
                    <div className="flex items-center space-x-2">
                        <StyledSwitch
                            id="newsupdates"
                            checked={formData.newsupdates}
                            onCheckedChange={(checked) =>
                                setFormData(prev => ({ ...prev, newsupdates: checked }))
                            }
                        />
                        <label
                            htmlFor="newsupdates"
                            className="font-inter text-sm text-ps-blue-dark cursor-pointer"
                        >
                            Houd mij op de hoogte van nieuws en updates
                        </label>
                    </div>

                    {/* Submit & Back Buttons */}
                    <div className="flex flex-col space-y-4">
                        <Button
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-ps-blue-dark hover:opacity-80 text-white flex items-center justify-center gap-2 h-12 font-inter"
                        >
                            {isLoading ? 'Bezig met registreren...' : 'Registreren'}
                            <ChevronRight className="h-4 w-4" />
                        </Button>

                        <Button
                            type="button"
                            variant="link"
                            onClick={() => navigate('/')}
                            className="text-ps-blue-dark hover:text-ps-blue font-medium flex items-center justify-center gap-2"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Terug naar inloggen
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Register;